import { FormikErrors } from 'formik';
import { useEffect, useMemo, useRef } from 'react';
import styles from './FilterPanel.module.scss';

import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

interface Props<T> {
  name: keyof T;
  allValues?: string[];
  formValues: T;
  formSetField: (
    field: string,
    value: string[] | boolean,
    shouldValidate?: boolean | undefined
  ) => Promise<void> | Promise<FormikErrors<T>>;
  formHandleChange: (e: string | React.ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
}
export const CheckboxAll = <T,>({ name, allValues, formValues, formSetField, formHandleChange, disabled }: Props<T>) => {
  const allValueName = `${String(name)}All` as keyof T;
  const { t } = useTranslation();

  const checkboxLabelAll = {
    country: t('CheckboxLabelAllCountry'),
    status: t('CheckboxLabelAllStatus'),
    type: t('CheckboxLabelAllType'),
    fleetGroup: t('CheckboxLabelAllFleetGroup'),
  };
  const values = formValues[name];
  const valuesAll = formValues && formValues[allValueName];

  const checkboxRef = useRef<HTMLInputElement>(null);

  const allValuesChecked = useMemo(() => {
    const result =
      Array.isArray(values) &&
      values.length === allValues?.length &&
      values.every((v) => allValues?.includes(v)) &&
      allValues?.every((v) => values.includes(v));

    if (Boolean(valuesAll) || result) {
      formSetField(allValueName as string, true);
    }
    return result;
  }, [formValues, allValues, values, valuesAll]);

  const isIndeterminate = useMemo(() => {
    return Array.isArray(values) && values.length > 0 && !allValuesChecked && !valuesAll;
  }, [formValues, allValuesChecked, values, valuesAll]);

  useEffect(() => {
    if (checkboxRef.current) checkboxRef.current.indeterminate = isIndeterminate;
  }, [isIndeterminate]);

  const checkboxAll = useMemo(() => {
    return allValues ? (
      <label key={allValueName as string} className={styles.checkboxLabel} aria-disabled={disabled}>
        <input
          disabled={disabled}
          ref={checkboxRef}
          id={allValueName as string}
          type="checkbox"
          name={allValueName as string}
          value={allValues}
          className={clsx(styles.checkboxInput, styles.checkboxAll)}
          checked={Boolean(valuesAll) || allValuesChecked}
          onChange={(value) => {
            if (value.target.checked) {
              formSetField(allValueName as string, true);
              formSetField(name as string, allValues);
            } else {
              formSetField(allValueName as string, false);
              formSetField(name as string, []);
            }
            formHandleChange(value.target.value);
          }}
        />
        {checkboxLabelAll[name as keyof typeof checkboxLabelAll]}
      </label>
    ) : null;
  }, [allValues, formValues]);

  return checkboxAll;
};
